import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Form, Table, Button } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import Select from "react-select";
import moment from "moment";
import { startCase } from "lodash";

import ParlayModal from "./ParlayModal";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { formatDate, toFixedWithoutRounding } from "../../utils/constants";
import AuthContext from "../../context/AuthContext";

const FMT = "YYYY-MM-DD HH:mm:ss";

const currencyOptions = [{ value: "pbu", label: "PBU" }];

const legLabel = (count) =>
  count === 1
    ? "Single"
    : count === 2
      ? "Double"
      : count === 3
        ? "Treble"
        : count > 3
          ? "Accumulator"
          : "";

const ParlayBetList = () => {
  const { user } = useContext(AuthContext);

  const DEFAULT_FILTER = {
    page: 1,
    limit: 100,
    betType: "parlay",
    status: "",
    keyword: "",
    stake: "",
    userType: user?.userType,
    id: user?._id,
    fromPeriod: moment().subtract(1, "day").format(FMT),
    toPeriod: moment().format(FMT),
  };

  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [data, setData] = useState({});
  const [show, setShow] = useState(false);
  const [selectedBets, setSelectedBets] = useState([]);

  const getData = async (activeFilter) => {
    const { status, data: res } = await apiGet(
      apiPath.betHistory,
      activeFilter,
    );
    if (status === 200 && res.success) setData(res.results);
  };

  useEffect(() => {
    getData(filter);
  }, [filter.page]);

  const setField = (key) => (e) =>
    setFilter((prev) => ({ ...prev, [key]: e.target.value }));

  const handleLimitChange = (e) =>
    setFilter((prev) => ({ ...prev, limit: Number(e.target.value) }));

  const handleCurrencyChange = (selected) =>
    setFilter((prev) => ({ ...prev, currency: selected?.value || "" }));

  const handleFromChange = (e) => {
    if (!e.target.value) return;
    setFilter((prev) => ({
      ...prev,
      fromPeriod: moment(e.target.value).startOf("day").format(FMT),
    }));
  };

  const handleToChange = (e) => {
    if (!e.target.value) return;
    setFilter((prev) => ({
      ...prev,
      toPeriod: moment(e.target.value).endOf("day").format(FMT),
    }));
  };

  const handlePageClick = (event) =>
    setFilter((prev) => ({ ...prev, page: event.selected + 1 }));

  const handleSearch = () => {
    if (filter.page !== 1)
      setFilter((prev) => ({ ...prev, page: 1 })); // effect refetches
    else getData(filter);
  };

  const applyPreset = (preset) => {
    const base = preset === "today" ? moment() : moment().subtract(1, "day");
    const nextFilter = {
      ...filter,
      page: 1,
      fromPeriod: base.clone().startOf("day").format(FMT),
      toPeriod: (preset === "today" ? moment() : base)
        .clone()
        .endOf("day")
        .format(FMT),
    };
    setFilter(nextFilter);
    getData(nextFilter);
  };

  const handleShowDefault = () => {
    setFilter(DEFAULT_FILTER);
    getData(DEFAULT_FILTER);
  };

  const openBetsList = (item) => {
    setSelectedBets(item?.bets || []);
    setShow(true);
  };

  const rows = data?.data || [];
  const totalRecords = data?.totalDocs || 0;
  const pageCount = data?.totalPages || 0;

  const getNet = (item) =>
    item?.isDeclared
      ? item.result === "loss"
        ? -Number(item?.loseAmount || 0)
        : Number(item?.profitAmount || 0)
      : 0;

  const pageTotalPL = rows.reduce((sum, item) => sum + getNet(item), 0);

  return (
    <div>
      <div className="user_data_div p-1">
        <section className="main-inner-outer py-3">
          <Container fluid>
            <div className="inner-wrapper">
              <div className="common-container">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h2 className="mb-0">Parlay Bets list</h2>
                  <div>
                    <span className="me-3">
                      Total Records: <strong>{totalRecords}</strong>
                    </span>
                    <span>
                      Page Total PL:{" "}
                      <strong
                        className={
                          pageTotalPL < 0 ? "text-danger" : "text-success"
                        }
                      >
                        {toFixedWithoutRounding(pageTotalPL)}
                      </strong>
                    </span>
                  </div>
                </div>

                <Form onSubmit={(e) => e.preventDefault()}>
                  <div className="bet_status border-0 mb-0">
                    <Row>
                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec">
                          <Form.Label className="mt-2 me-2">
                            Bet Status:
                          </Form.Label>
                          <Form.Select
                            className="small_select"
                            value={filter.status}
                            onChange={setField("status")}
                            aria-label="Default select example"
                          >
                            <option value="">All</option>
                            <option value="completed">Settled</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="voided">Voided</option>
                          </Form.Select>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec bet-period">
                          <Form.Label className="mt-2 me-2">From</Form.Label>
                          <Form.Group className="form-group">
                            <Form.Control
                              type="date"
                              max={moment().format("YYYY-MM-DD")}
                              value={moment(filter.fromPeriod).format(
                                "YYYY-MM-DD",
                              )}
                              onChange={handleFromChange}
                            />
                            <Form.Control
                              className="small_form_control"
                              type="text"
                              placeholder="00:00"
                              disabled
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="bet-sec bet-period">
                          <Form.Label className="mt-2 me-2">To</Form.Label>
                          <Form.Group className="form-group">
                            <Form.Control
                              type="date"
                              min={moment(filter.fromPeriod).format(
                                "YYYY-MM-DD",
                              )}
                              max={moment().format("YYYY-MM-DD")}
                              value={moment(filter.toPeriod).format(
                                "YYYY-MM-DD",
                              )}
                              onChange={handleToChange}
                            />
                            <Form.Control
                              className="small_form_control"
                              type="text"
                              placeholder="23:59"
                              disabled
                            />
                          </Form.Group>
                        </div>
                      </Col>

                      <Col lg={2} sm={3} className="mb-lg-0 mb-1">
                        <div className="history-btn mb-3">
                          <ul className="list-unstyled mb-0">
                            <li>
                              <Button
                                className="me-0 theme_light_btn"
                                onClick={() => applyPreset("today")}
                              >
                                Just For Today
                              </Button>
                            </li>
                            <li>
                              <Button
                                className="me-0 theme_light_btn"
                                onClick={() => applyPreset("yesterday")}
                              >
                                From Yesterday
                              </Button>
                            </li>
                            <li>
                              <Button
                                className="theme_light_btn theme_dark_btn"
                                onClick={handleSearch}
                              >
                                Get History
                              </Button>
                            </li>
                          </ul>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Form>

                <div className="account-table account-tabel-style batting-table">
                  <div className="responsive">
                    <Table>
                      <thead>
                        <tr>
                          <th scope="col">SA ID</th>
                          <th scope="col">SM ID</th>
                          <th scope="col">MA ID</th>
                          <th scope="col">PL ID</th>
                          <th scope="col">Bet ID</th>
                          <th scope="col">Bet taken</th>
                          <th scope="col">Bet Settled</th>
                          <th scope="col">IP Address</th>
                          <th scope="col">List Bets</th>
                          <th scope="col">Event</th>
                          <th scope="col">Type</th>
                          <th scope="col">Odds req</th>
                          <th scope="col">Stake</th>
                          <th scope="col">Profit/Loss</th>
                          <th scope="col">Status</th>
                          <th scope="col">Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length > 0 ? (
                          rows.map((item, index) => {
                            const ip =
                              (item?.ipAddress &&
                                item.ipAddress.replace("::ffff:", "")) ||
                              "-";
                            const net = getNet(item);
                            return (
                              <tr key={item?._id || index}>
                                <td>{item?.superAdmin?.username || "-"}</td>
                                <td>{item?.superMaster?.username || "-"}</td>
                                <td>{item?.master?.username || "-"}</td>
                                <td>{item?.clientName || "-"}</td>
                                <td
                                  style={{
                                    textDecoration: "underline",
                                    color: "#2779bf",
                                  }}
                                >
                                  {item?.matchBetId || "-"}
                                </td>
                                <td>
                                  {formatDate(
                                    item?.timeInserted,
                                    user?.timeZone,
                                  )}
                                </td>
                                <td>
                                  {item?.isDeclared
                                    ? formatDate(
                                        item?.settledDate || item?.updatedAt,
                                        user?.timeZone,
                                      )
                                    : ""}
                                </td>
                                <td>{ip}</td>
                                <td>
                                  <Button
                                    size="sm"
                                    className="theme_light_btn"
                                    onClick={() => openBetsList(item)}
                                  >
                                    List
                                  </Button>
                                </td>
                                <td>
                                  parlay{" "}
                                  <span className="angle_unicode">▸</span>
                                  <strong>
                                    {" "}
                                    {legLabel(item?.bets?.length)}
                                  </strong>
                                </td>
                                <td>Parlay</td>
                                <td>
                                  {toFixedWithoutRounding(item?.bhav) || "-"}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {item?.amount} <strong>(PBU)</strong>
                                </td>
                                <td>
                                  {item?.isDeclared ? (
                                    <span
                                      className={
                                        net < 0 ? "text-danger" : "text-success"
                                      }
                                    >
                                      {toFixedWithoutRounding(net)}
                                    </span>
                                  ) : (
                                    "..."
                                  )}
                                </td>
                                <td>
                                  {item?.isDeclared
                                    ? startCase(item?.status || item?.result)
                                    : "Open"}
                                </td>
                                <td></td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={16}>
                              You have no bets in this time period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <p className="table-other mt-2">
                    Bet List is shown net of commission.
                  </p>

                  {rows.length > 0 && (
                    <div className="bottom-pagination">
                      <ReactPaginate
                        breakLabel="..."
                        nextLabel="Next"
                        previousLabel="Prev"
                        forcePage={(filter.page || 1) - 1}
                        onPageChange={handlePageClick}
                        pageRangeDisplayed={10}
                        pageCount={pageCount}
                        renderOnZeroPageCount={null}
                        activeClassName="p-1"
                        activeLinkClassName="pagintion-li"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Container>
        </section>
      </div>

      <ParlayModal
        isOpen={show}
        onClose={() => setShow(false)}
        data={selectedBets}
      />
    </div>
  );
};

export default ParlayBetList;
